/**
 * FrameSequencePlayer - 4DGS Frame-by-Frame Playback
 *
 * Matches the PlayCanvas gsplat-flipbook approach:
 * - Single entity with direct asset swap (no double-buffering)
 * - Only swaps when next frame's asset is fully loaded (no flicker)
 * - Disables splat reorder for performance ({ reorder: false })
 * - Sliding preload window with deferred unloads
 */
import * as pc from 'playcanvas';
export interface FrameSequenceConfig {
    frameUrls: string[]; // Array of PLY/SOG URLs
    fps?: number; // Playback FPS (default: 24)
    loop?: boolean; // Loop playback (default: true)
    preloadCount?: number; // Frames to preload ahead (default: 10)
    autoplay?: boolean; // Start playing immediately (default: false)
    rotation?: [number, number, number]; // Entity euler angles [pitch, yaw, roll] in degrees
}
export interface FrameSequencePlayerOptions {
    onFrameChange?: (frame: number, total: number) => void;
    onLoadProgress?: (loaded: number, total: number) => void;
    onError?: (error: string) => void;
}
type FramePlayerEventCallback = (...args: unknown[]) => void;
export class FrameSequencePlayer {
    private app: pc.Application;
    private frameUrls: string[];
    private frameAssets: Map<number, pc.Asset> = new Map();
    private entity: pc.Entity;
    private currentFrame: number = 0;
    private fps: number;
    private isPlaying: boolean = false;
    private loop: boolean;
    private preloadCount: number;
    private lastFrameTime: number = 0;
    private frameInterval: number;
    private loadingFrames: Set<number> = new Set();
    private destroyed: boolean = false;
    private listeners: Map<string, Set<FramePlayerEventCallback>> = new Map();
    constructor(app: pc.Application, config: FrameSequenceConfig, private options: FrameSequencePlayerOptions = {}) {
        this.app = app;
        this.frameUrls = config.frameUrls;
        this.fps = config.fps || 24;
        this.loop = config.loop !== false;
        this.preloadCount = config.preloadCount || 10;
        this.frameInterval = 1000 / this.fps;
        // Single entity — direct asset swap, no double-buffering
        // unified: true uses GSplatPlacement pathway for fast synchronous swaps
        this.entity = new pc.Entity('frameSequenceSplat');
        this.entity.addComponent('gsplat', { unified: true });
        if (config.rotation) {
            this.entity.setEulerAngles(config.rotation[0], config.rotation[1], config.rotation[2]);
        }
        this.entity.enabled = false;
        this.app.root.addChild(this.entity);
        // Start preloading
        this.preloadInitialFrames();
        // Auto-play if configured
        if (config.autoplay) {
            this.preloadFrame(0).then(() => {
                if (!this.destroyed) {
                    this.play();
                }
            });
        }
    }
    /**
     * Preload initial frames for smooth playback start
     */
    private async preloadInitialFrames(): Promise<void> {
        const framesToLoad = Math.min(this.preloadCount, this.frameUrls.length);
        const loadPromises: Promise<pc.Asset | null>[] = [];
        for (let i = 0; i < framesToLoad; i++) {
            loadPromises.push(this.preloadFrame(i));
        }
        await Promise.all(loadPromises);
        this.options.onLoadProgress?.(framesToLoad, this.frameUrls.length);
    }
    /**
     * Preload a specific frame. Uses { reorder: false } to skip expensive
     * splat sort-order computation (matching PlayCanvas flipbook approach).
     */
    async preloadFrame(index: number): Promise<pc.Asset | null> {
        if (this.destroyed)
            return null;
        if (index < 0 || index >= this.frameUrls.length)
            return null;
        if (this.frameAssets.has(index))
            return this.frameAssets.get(index)!;
        if (this.loadingFrames.has(index))
            return null;
        this.loadingFrames.add(index);
        return new Promise((resolve) => {
            const url = this.frameUrls[index];
            const asset = new pc.Asset(`frame_${index}`, 'gsplat', { url }, { reorder: false });
            asset.on('load', () => {
                if (!this.destroyed) {
                    this.frameAssets.set(index, asset);
                    this.loadingFrames.delete(index);
                }
                resolve(asset);
            });
            asset.on('error', (err: string) => {
                console.error(`Failed to load frame ${index}:`, err);
                this.loadingFrames.delete(index);
                this.options.onError?.(`Failed to load frame ${index}: ${err}`);
                resolve(null);
            });
            this.app.assets.add(asset);
            this.app.assets.load(asset);
        });
    }
    /**
     * Unload a frame to free memory
     */
    unloadFrame(index: number): void {
        const asset = this.frameAssets.get(index);
        if (asset) {
            this.app.assets.remove(asset);
            asset.unload();
            this.frameAssets.delete(index);
        }
    }
    /**
     * Update the preload window based on current frame
     */
    private updatePreloadWindow(): void {
        // Preload upcoming frames
        for (let i = 1; i <= this.preloadCount; i++) {
            const frameIndex = this.loop
                ? (this.currentFrame + i) % this.frameUrls.length
                : this.currentFrame + i;
            if (frameIndex < this.frameUrls.length) {
                this.preloadFrame(frameIndex);
            }
        }
        // Unload old frames (keep 2 behind current)
        const keepBehind = 2;
        for (const [index] of this.frameAssets) {
            const distance = this.currentFrame - index;
            if (distance > keepBehind && distance < this.frameUrls.length - this.preloadCount) {
                this.unloadFrame(index);
            }
        }
    }
    /**
     * Display a specific frame via direct asset swap on a single entity.
     * Only swaps if the asset is already loaded — holds the current frame
     * if the next one isn't ready (prevents flicker).
     * Returns true if the frame was displayed, false if skipped.
     */
    private displayFrame(index: number): boolean {
        if (this.destroyed) return false;
        const asset = this.frameAssets.get(index);
        if (!asset || !asset.loaded) return false;
        // Direct asset swap on single entity (synchronous path in PlayCanvas engine:
        // destroyInstance → new GSplatPlacement → addToLayers, all in one tick)
        const gsplat = this.entity.gsplat;
        if (gsplat) {
            gsplat.asset = asset;
        }
        this.entity.enabled = true;
        this.currentFrame = index;
        this.emit('frameChange', index, this.frameUrls.length);
        this.options.onFrameChange?.(index, this.frameUrls.length);
        this.updatePreloadWindow();
        return true;
    }
    /**
     * Display a frame, waiting for it to load if necessary.
     * Used by explicit user actions (setFrame, stop) where we want to
     * guarantee the frame appears even if not yet preloaded.
     */
    private async displayFrameAsync(index: number): Promise<void> {
        if (this.destroyed) return;
        if (this.displayFrame(index)) return;
        // Not loaded yet — trigger preload and display when ready
        const asset = await this.preloadFrame(index);
        if (asset && !this.destroyed) {
            this.displayFrame(index);
        }
    }
    /**
     * Main update loop — called every render frame.
     * Only advances if the next frame's asset is fully loaded (gate).
     */
    update(dt: number): void {
        if (!this.isPlaying || this.destroyed)
            return;
        const now = performance.now();
        const elapsed = now - this.lastFrameTime;
        if (elapsed >= this.frameInterval) {
            let nextFrame = this.currentFrame + 1;
            if (nextFrame >= this.frameUrls.length) {
                if (this.loop) {
                    nextFrame = 0;
                }
                else {
                    this.pause();
                    this.emit('complete');
                    return;
                }
            }
            // Only advance if the next frame is loaded — hold current frame otherwise
            if (this.displayFrame(nextFrame)) {
                this.lastFrameTime = now - (elapsed % this.frameInterval);
            }
        }
    }
    // ============================================================================
    // Public Playback API
    // ============================================================================
    play(): void {
        if (this.isPlaying || this.destroyed)
            return;
        this.isPlaying = true;
        this.lastFrameTime = performance.now();
        // Show current frame if not already visible
        if (!this.entity.enabled) {
            this.displayFrameAsync(this.currentFrame);
        }
        this.emit('play');
    }
    pause(): void {
        this.isPlaying = false;
        this.emit('pause');
    }
    stop(): void {
        this.isPlaying = false;
        this.currentFrame = 0;
        this.displayFrameAsync(0);
        this.emit('stop');
    }
    setFrame(index: number): void {
        if (index < 0)
            index = 0;
        if (index >= this.frameUrls.length)
            index = this.frameUrls.length - 1;
        this.displayFrameAsync(index);
    }
    nextFrame(): void {
        let next = this.currentFrame + 1;
        if (next >= this.frameUrls.length) {
            next = this.loop ? 0 : this.frameUrls.length - 1;
        }
        this.setFrame(next);
    }
    previousFrame(): void {
        let prev = this.currentFrame - 1;
        if (prev < 0) {
            prev = this.loop ? this.frameUrls.length - 1 : 0;
        }
        this.setFrame(prev);
    }
    // ============================================================================
    // Getters/Setters
    // ============================================================================
    getCurrentFrame(): number {
        return this.currentFrame;
    }
    getTotalFrames(): number {
        return this.frameUrls.length;
    }
    getProgress(): number {
        return this.frameUrls.length > 1
            ? this.currentFrame / (this.frameUrls.length - 1)
            : 0;
    }
    setProgress(progress: number): void {
        const frame = Math.round(progress * (this.frameUrls.length - 1));
        this.setFrame(frame);
    }
    getFps(): number {
        return this.fps;
    }
    setFps(fps: number): void {
        this.fps = fps;
        this.frameInterval = 1000 / fps;
    }
    getIsPlaying(): boolean {
        return this.isPlaying;
    }
    setLoop(loop: boolean): void {
        this.loop = loop;
    }
    getLoop(): boolean {
        return this.loop;
    }
    // ============================================================================
    // Transform methods
    // ============================================================================
    setPosition(x: number, y: number, z: number): void {
        this.entity.setPosition(x, y, z);
    }
    setRotation(x: number, y: number, z: number): void {
        this.entity.setEulerAngles(x, y, z);
    }
    setScale(x: number, y: number, z: number): void {
        this.entity.setLocalScale(x, y, z);
    }
    // ============================================================================
    // Event Emitter
    // ============================================================================
    on(event: string, callback: FramePlayerEventCallback): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);
    }
    off(event: string, callback: FramePlayerEventCallback): void {
        this.listeners.get(event)?.delete(callback);
    }
    private emit(event: string, ...args: unknown[]): void {
        this.listeners.get(event)?.forEach(cb => cb(...args));
    }
    // ============================================================================
    // Cleanup
    // ============================================================================
    destroy(): void {
        this.destroyed = true;
        this.isPlaying = false;
        // Unload all assets
        for (const [index] of this.frameAssets) {
            this.unloadFrame(index);
        }
        // Remove entity
        this.entity.destroy();
        this.listeners.clear();
    }
}
